import React from 'react';

type Severity = 'red' | 'orange' | 'green' | 'blue';

interface Reason {
    reason: string;
    severity: Severity;
}

interface AvailabilityOverlayProps {
    reasons: Reason[];
    position: {
        top: number;
        left: number;
    };
    isVisible: boolean;
}

/**
 * An overlay component to display conflict reasons next to a dragged item.
 * Each reason is colored based on its severity.
 */
const AvailabilityOverlay: React.FC<AvailabilityOverlayProps> = ({ reasons = [], position, isVisible }) => {
    if (!isVisible || !Array.isArray(reasons) || reasons.length === 0) {
        return null; // Don't render if not visible or no reasons
    }

    // Calculate adjusted position to prevent going off-screen
    const adjustedTop = Math.max(10, Math.min(position.top, window.innerHeight - 200)); // Keep at least 10px from top and 200px from bottom
    const adjustedLeft = Math.max(10, Math.min(position.left, window.innerWidth - 350)); // Keep at least 10px from left and 350px from right

    const style = {
        position: 'absolute' as 'absolute',
        top: `${adjustedTop}px`,
        left: `${adjustedLeft}px`,
        zIndex: 60, // Ensure it's above other elements like the drag marker
        maxWidth: '320px', // Limit width to prevent overflow
        minWidth: '200px', // Ensure minimum readable width
    };

    // Function to get text color class based on severity
    const getSeverityClass = (severity: Severity) => {
        if (severity === 'red') {
            return 'text-red-700'; // Red for errors/cannot
        } else if (severity === 'orange') {
            return 'text-orange-700'; // Orange for warnings/prefer_away
        }
        return 'text-gray-700'; // Default for unknown severity
    };

    return (
        <div
            style={style}
            // Keep white background, standard border
            className={`bg-white text-xs rounded shadow-lg p-3 border border-gray-300 pointer-events-none`}
            role="alert"
            aria-live="polite"
        >
            <div className="font-semibold text-gray-800 mb-2 border-b border-gray-200 pb-1">
                Conflicts Found
            </div>
            <ul className="space-y-1">
                {reasons.map((reasonObj, index) => (
                    // Apply severity color to each list item with better formatting
                    <li key={index} className={`${getSeverityClass(reasonObj.severity)} flex items-start`}>
                        <span className={`inline-block w-2 h-2 rounded-full mr-2 mt-1.5 flex-shrink-0 ${reasonObj.severity === 'red' ? 'bg-red-500' :
                            reasonObj.severity === 'orange' ? 'bg-orange-500' :
                                'bg-gray-500'
                            }`}></span>
                        <span className="leading-relaxed">{reasonObj.reason}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export default AvailabilityOverlay; 